import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';
import Layout from '../../components/Layout';
import departmentService, { CreateDepartmentDTO } from '../../services/departmentService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './DepartmentCreatePage.css';

interface FieldErrors {
  departmentName?: string;
  departmentCode?: string;
  departmentShortName?: string;
}

const DepartmentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<CreateDepartmentDTO>({
    departmentName: '',
    departmentCode: '',
    departmentShortName: '',
    departmentType: 'Sales',
    description: '',
  });

  const departmentTypes = ['Sales', 'Finance', 'Warehouse', 'HR', 'Operations', 'IT'];
  const MAX_LENGTHS = {
    departmentName: 30,
    departmentCode: 10,
    departmentShortName: 3,
    description: 200,
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Enforce max length
    let finalValue = value;
    if (name === 'departmentName' && value.length > MAX_LENGTHS.departmentName) {
      finalValue = value.substring(0, MAX_LENGTHS.departmentName);
    } else if (name === 'departmentCode' && value.length > MAX_LENGTHS.departmentCode) {
      finalValue = value.substring(0, MAX_LENGTHS.departmentCode);
    } else if (name === 'departmentShortName' && value.length > MAX_LENGTHS.departmentShortName) {
      finalValue = value.substring(0, MAX_LENGTHS.departmentShortName);
    } else if (name === 'description' && value.length > MAX_LENGTHS.description) {
      finalValue = value.substring(0, MAX_LENGTHS.description);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name as keyof FieldErrors]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.departmentName.trim()) {
      errors.departmentName = 'Department name is required';
      isValid = false;
    }
    if (!formData.departmentCode.trim()) {
      errors.departmentCode = 'Department code is required';
      isValid = false;
    }
    if (!formData.departmentShortName.trim()) {
      errors.departmentShortName = 'Short name is required';
      isValid = false;
    }

    setFieldErrors(errors);

    // Display error message in error div if validation fails
    if (!isValid) {
      setError('Please correct the errors below before saving.');
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await departmentService.createDepartment(formData);
      navigate('/organization/departments', {
        state: { message: 'Department created successfully!' },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('department.failedToLoadDepartment')).message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/organization/departments')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('department.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/organization/departments')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger">
            {error}
             <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="create-content">
          {/* Form Card */}
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('department.details')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">
                      {t('department.departmentName')} <span className="required">*</span>
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.departmentName.length}/{MAX_LENGTHS.departmentName}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.departmentName ? 'is-invalid' : ''}`}
                      name="departmentName"
                      value={formData.departmentName}
                      onChange={handleInputChange}
                      placeholder={t('department.departmentName')}
                      maxLength={MAX_LENGTHS.departmentName}
                    />
                    {fieldErrors.departmentName && (
                      <small className="form-error">{fieldErrors.departmentName}</small>
                    )}
                    <small className="form-helper">e.g., Sales, Finance, HR, Operations</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('department.code')} <span className="required">*</span>
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.departmentCode.length}/{MAX_LENGTHS.departmentCode}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.departmentCode ? 'is-invalid' : ''}`}
                      name="departmentCode"
                      value={formData.departmentCode}
                      onChange={handleInputChange}
                      placeholder={t('department.code')}
                      maxLength={MAX_LENGTHS.departmentCode}
                    />
                    {fieldErrors.departmentCode && (
                      <small className="form-error">{fieldErrors.departmentCode}</small>
                    )}
                    <small className="form-helper">Unique identifier for the department</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('department.shortName')} <span className="required">*</span>
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.departmentShortName.length}/{MAX_LENGTHS.departmentShortName}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.departmentShortName ? 'is-invalid' : ''}`}
                      name="departmentShortName"
                      value={formData.departmentShortName}
                      onChange={handleInputChange}
                      placeholder="e.g., SAL, FIN, HR"
                      maxLength={MAX_LENGTHS.departmentShortName}
                    />
                    {fieldErrors.departmentShortName && (
                      <small className="form-error">{fieldErrors.departmentShortName}</small>
                    )}
                    <small className="form-helper">Short abbreviation for quick reference</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('department.type')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="departmentType"
                      value={formData.departmentType}
                      onChange={handleInputChange}
                    >
                      {departmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <small className="form-helper">Select the primary category of this department</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('common.description')}
                      <span style={{ float: 'right', fontSize: '12px', color: '#9ca3af' }}>
                        {formData.description.length}/{MAX_LENGTHS.description}
                      </span>
                    </label>
                    <textarea
                      className="form-textarea"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={t('common.description')}
                      maxLength={MAX_LENGTHS.description}
                      rows={4}
                    />
                    <small className="form-helper">Add details about the department's responsibilities and scope</small>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('common.info')}</h3>
              </div>
              <div className="create-card-body">
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  <p>
                    <strong>{t('common.info')}:</strong>
                  </p>
                  <ul style={{ paddingLeft: '16px', margin: '8px 0' }}>
                    <li>Department name must be unique within the organization</li>
                    <li>Department code is used for system references and reports</li>
                    <li>Short names help in quick identification in lists</li>
                    <li>Department type helps categorize and organize departments</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DepartmentCreatePage;
